import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Calendar, MapPin, Clock } from 'lucide-react';
import { getEvents } from '../utils/api';

gsap.registerPlugin(ScrollTrigger);

const Activities = () => {
    const [events, setEvents] = useState([]);

    useEffect(() => {
        const fetchEvents = async () => {
            try {
                const data = await getEvents();
                setEvents(data);
            } catch (error) {
                console.error('Error fetching events:', error);
            }
        };
        fetchEvents();

        const sections = document.querySelectorAll('.animate-section');
        sections.forEach((section) => {
            gsap.fromTo(section, { opacity: 0, y: 50 }, { opacity: 1, y: 0, duration: 1, scrollTrigger: { trigger: section, start: 'top 80%', toggleActions: 'play none none reverse' } });
        });
    }, []);

    const activities = [
        { title: 'Annual Sports Day', description: 'Inter-house competitions, athletics, and team sports', image: 'images/1_67.jpeg' },
        { title: 'Cultural Festival', description: 'Dance, drama, music, and art exhibitions', image: 'images/1_04.jpeg' },
        { title: 'Science Exhibition', description: 'Student projects and innovative experiments', image: 'images/Science Exibition.png' },
        { title: 'Field Trips', description: 'Educational excursions to museums, historical sites', image: 'images/1_29.jpeg' },
        { title: 'Parents Orientation', description: 'Regular Updates on their ward progess', image: 'images/1_102.jpg' },
        { title: 'Art & Craft Workshops', description: 'Creative expression through various art forms', image: 'images/1_26.jpeg' }
    ];

    return (
        <div className="min-h-screen pt-24" data-testid="activities-page">
            <section className="relative py-20 overflow-hidden" style={{ backgroundImage: `url(https://images.unsplash.com/photo-1427504494785-3a9ca7044f45?w=1920)`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
                <div className="absolute inset-0 bg-gradient-to-br from-violet-900/90 via-purple-800/90 to-blue-900/90"></div>
                <div className="relative z-10 container mx-auto px-4 md:px-8 text-center text-white">
                    <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
                        <h1 className="font-outfit font-bold text-5xl md:text-7xl mb-6 text-white">Activities & Events</h1>
                        <p className="font-dm-sans text-xl md:text-2xl max-w-3xl mx-auto text-white/95">Beyond academics - shaping well-rounded individuals</p>
                    </motion.div>
                </div>
            </section>

            <section className="py-24 bg-white animate-section">
                <div className="container mx-auto px-4 md:px-8">
                    <h2 className="font-outfit font-bold text-4xl text-center mb-16 text-black">Our Activities</h2>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {activities.map((activity, index) => (
                            <motion.div key={index} className="group relative overflow-hidden rounded-3xl shadow-sm hover:shadow-xl transition-all" whileHover={{ y: -10 }} data-testid={`activity-card-${index}`}>
                                <div className="aspect-[4/3] overflow-hidden">
                                    <img src={activity.image} alt={activity.title} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110" />
                                </div>
                                <div className="p-6 bg-white">
                                    <h3 className="font-outfit font-bold text-xl mb-2">{activity.title}</h3>
                                    <p className="font-dm-sans text-sm text-slate-600">{activity.description}</p>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            <section className="py-24 bg-red-600 animate-section">
                <div className="container mx-auto px-4 md:px-8">
                    <h2 className="font-outfit font-bold text-4xl text-primary text-center mb-16">Upcoming Events</h2>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
                        {events.map((event, index) => (
                            <motion.div key={event.id} className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-xl transition-all" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.1 }} data-testid={`event-card-${index}`}>
                                <Calendar className="w-10 h-10 text-primary mb-4" />
                                <h3 className="font-outfit font-bold text-xl mb-3">{event.title}</h3>
                                <p className="font-dm-sans text-sm text-slate-600 mb-4">{event.description}</p>
                                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                                    <Clock className="w-4 h-4" />
                                    <span>{event.date}</span>
                                </div>
                                {event.location && (
                                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                        <MapPin className="w-4 h-4" />
                                        <span>{event.location}</span>
                                    </div>
                                )}
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Activities;